/**
 * *Nová hypotéza*: a title, the claim, and the focus entities (PRD-0075 R-E1). Assessments come
 * later, on the hypothesis page. The feature submits; a refusal comes back as its message.
 */
import { type FormEvent, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EntityOption, EntitySearch } from '@/components/entity-picker/EntityPicker'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { AnalyticHypothesisCreate } from '@/types/analytic-hypothesis'
import { FocusEntityPicker } from './FocusEntityPicker'
import { useSubmission } from './use-submission'

const COPY = 'analyticHypotheses.create'

export interface CreateHypothesisDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Resolves to null once created, or to the message of why not. */
  onSubmit: (body: AnalyticHypothesisCreate) => Promise<string | null>
  entitySearch: EntitySearch
}

interface Draft {
  title: string
  claim: string
  focus: EntityOption[]
}

const EMPTY: Draft = { title: '', claim: '', focus: [] }

export function CreateHypothesisDialog({
  open,
  onOpenChange,
  onSubmit,
  entitySearch,
}: CreateHypothesisDialogProps) {
  const { t } = useTranslation()
  const titleId = useId()
  const claimId = useId()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const submission = useSubmission(t(`${COPY}.failed`))
  const ready = draft.title.trim().length > 0 && draft.claim.trim().length > 0

  const changeOpen = (next: boolean) => {
    if (!next) {
      setDraft(EMPTY)
      submission.clearError()
    }
    onOpenChange(next)
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!ready || submission.pending) return
    const body = {
      title: draft.title.trim(),
      claim: draft.claim.trim(),
      focus_uids: draft.focus.map(entity => entity.uid),
    }
    if (await submission.run(() => onSubmit(body))) changeOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t(`${COPY}.title`)}</DialogTitle>
            <DialogDescription>{t(`${COPY}.description`)}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={titleId}>{t(`${COPY}.titleLabel`)}</Label>
            <Input
              id={titleId}
              value={draft.title}
              maxLength={255}
              onChange={event => setDraft({ ...draft, title: event.target.value })}
              placeholder={t(`${COPY}.titlePlaceholder`)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={claimId}>{t(`${COPY}.claimLabel`)}</Label>
            <Textarea
              id={claimId}
              value={draft.claim}
              rows={3}
              onChange={event => setDraft({ ...draft, claim: event.target.value })}
              placeholder={t(`${COPY}.claimPlaceholder`)}
            />
          </div>
          <FocusEntityPicker
            search={entitySearch}
            selected={draft.focus}
            onChange={focus => setDraft({ ...draft, focus })}
          />
          {submission.error && (
            <p role="alert" className="text-xs text-destructive">
              {submission.error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => changeOpen(false)}>
              {t(`${COPY}.cancel`)}
            </Button>
            <Button type="submit" disabled={!ready || submission.pending}>
              {submission.pending ? t(`${COPY}.submitting`) : t(`${COPY}.submit`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
